import { resolveNoddiDualTldUrl, sentryFirstPartyTunnel } from '@navio/observability';
import {
  breadcrumbsIntegration,
  browserTracingIntegration,
  extraErrorDataIntegration,
  replayIntegration,
  type ErrorEvent,
  type EventHint
} from '@sentry/react';
import { isAxiosError } from 'noddi-util';
import {
  isExtensionRequestTimeoutRejection,
  isFacebookInAppBridgeGone,
  isInjectedScriptStackOverflow
} from './sentryNoise';
import { linkSentryEventToReplays } from './sentryReplayLinks';
import { environment } from './src/constants/environment';

const dsn = import.meta.env.VITE_APP_SENTRY_DSN;
const tunnel = resolveNoddiDualTldUrl(import.meta.env.VITE_APP_SENTRY_TUNNEL) ?? sentryFirstPartyTunnel(dsn);

// Our own API, for replay network detail (bodies/headers are URL-gated).
const apiUrls = [/^https:\/\/([a-z0-9-]+\.)?api\.noddi\.(co|no)/, 'localhost'];

// Drop unhandled Axios rejections: global capture duplicates cache reports
// and bypasses the expected-failure filtering in `apiErrorSeverity`.
// Non-API objects reach Sentry unchanged through `logQueryCacheError`, so
// its SDK deduplicates them by identity before this hook.
//
// The other drops are browser-injected noise; `sentryNoise` states each case.
const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const mechanism = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanism === 'onunhandledrejection' && isAxiosError(hint.originalException)) {
    return null;
  }
  if (isInjectedScriptStackOverflow(event, window.location.origin)) {
    return null;
  }
  if (isExtensionRequestTimeoutRejection(hint.originalException)) {
    return null;
  }
  if (isFacebookInAppBridgeGone(event)) {
    return null;
  }
  // Last, so dropped noise never lands a marker on the replay timeline.
  return linkSentryEventToReplays(event);
};

const sentryConfig = {
  dsn,
  // Set here, not injected by the Sentry Vite plugin (`release.inject: false`
  // in `sentryOptions`): injection writes the release into every chunk, so the
  // vendor chunk would change hash on every deploy. Same value on Netlify:
  // both resolve to `COMMIT_REF`. Vitest defines no `__SOURCE_VERSION__`.
  release: typeof __SOURCE_VERSION__ === 'string' ? __SOURCE_VERSION__ : undefined,
  // Same-apex tunnel so Brave Shields never see ingest.sentry.io. Path is
  // `/api/<project>/envelope/` so Traefik can reverse-proxy without parsing
  // the envelope. The SDK posts to the tunnel URL verbatim, so
  // `sentryFirstPartyTunnel` appends `sentry_key` itself (the SDK only adds
  // auth params on its own ingest path).
  ...(tunnel ? { tunnel } : {}),
  integrations: [
    replayIntegration({
      // No masking. Sentry masks every text node, input and media element by
      // default, which leaves replays unreadable — we need to see exactly what
      // the user saw. Backoffice apps are staff-only; the customer app shows
      // the user their own data. Password-type inputs stay masked regardless.
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      maskAttributes: [],
      // The default `mask`/`block` selectors (`.sentry-mask`, `[data-sentry-block]`, …)
      // are merged with whatever we pass, never replaced, so opt out via unmask/unblock.
      unmask: ['*'],
      unblock: ['*'],
      // Replays record method/status/duration only until a URL is allow-listed.
      networkDetailAllowUrls: apiUrls,
      networkCaptureBodies: true,
      networkRequestHeaders: ['Content-Type', 'X-Navio-Source', 'X-Navio-Source-Version'],
      networkResponseHeaders: ['Content-Type', 'X-Request-Id']
    }),
    // Attach every non-standard property of a thrown Error as event context —
    // axios errors and ErrorWithCause carry most of their detail there.
    extraErrorDataIntegration({ depth: 10 }),
    //The Browser Tracing integration automatically instruments browser pageload/navigation actions as transactions, and captures requests, metrics and errors as spans.
    browserTracingIntegration(),
    breadcrumbsIntegration({
      console: true, // Capture console logs
      dom: true, // Capture DOM interactions
      fetch: true, // Capture fetch requests
      xhr: true // Capture XHR requests
    })
  ],
  beforeSend,

  environment,
  // Richer error context: IP/headers on the event, stacktraces on captureMessage,
  // deep serialization of `extra` (default 3 renders nested objects as "[Object]"),
  // and untruncated error messages / API error details (default 250 chars).
  sendDefaultPii: true,
  attachStacktrace: true,
  normalizeDepth: 10,
  maxValueLength: 8192,
  maxBreadcrumbs: 100,
  // Performance Monitoring
  //  Capture 10% of the transactions in production and 100% in development
  tracesSampleRate: environment === 'development' ? 1.0 : 0.1,
  //Session Replays turned off by default
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: apiUrls,
  enabled: environment !== 'local',
  // Ignore specific errors

  ignoreErrors: [
    'window.google.maps.Geocoder is not a constructor',
    "undefined is not a constructor (evaluating 'new window.google.maps.Geocoder')",
    "Cannot read properties of undefined (reading 'AutocompleteService')",
    'Error verifying pin code',
    // Instagram / other iOS in-app browsers inject a webkit bridge that is
    // missing on pagehide. Not application code.
    'window.webkit.messageHandlers',
    // Outlook / Bing SafeSearch injected script. Not application code.
    'Object Not Found Matching Id',
    // Cancellable browser work can reject after navigation; it is not an app failure.
    'AbortError: AbortError',
    // DuckDuckGo / extension bridge losing its tab on navigation. Not application code.
    'Invalid call to runtime.sendMessage()',
    // Mixpanel's IndexedDB upgrade when Safari denies the store. Third-party, non-fatal.
    /createObjectStore/,
    // Firebase's own IndexedDB writes losing their store when iOS clears or evicts
    // site data. It rejects inside the SDK with no app-side await to guard, and
    // remote config already falls back to its static values.
    'Database deleted by request of the user',
    // Same class: Firebase's IndexedDB open or read is aborted when the tab is closed or
    // the browser evicts the store mid-transaction. Remote config falls back to its
    // static values, so nothing downstream changes.
    /remoteconfig\/storage-(open|get)/,
    // Firebase Installations refusing to register while the device is offline.
    // Network state, not application code.
    'installations/app-offline',
    // Leaflet zoom animation finishing after the map unmounted.
    '_leaflet_pos',
    // Brave iOS user scripts (reader mode, YouTube quality, wallet) run in the page
    // before their `__firefox__` / `ethereum` bridge exists. Not application code.
    /__firefox__/,
    /window\.ethereum/
  ]
};

export default sentryConfig;
